import { useEffect, useState } from "react";
import adminApi, { getAdminToken, setAdminToken, clearAdminToken } from "@/lib/adminApi";
import { fmtMoney } from "@/lib/format";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Plus, SignOut, ShieldCheck, PencilSimple, LockKey, Power, Trash, ChartBar,
} from "@phosphor-icons/react";

const PAGE_GROUPS = [
  {
    label: "Labour",
    pages: [
      { key: "dashboard", label: "Dashboard" },
      { key: "attendance", label: "Attendance" },
      { key: "employees", label: "Labourers" },
      { key: "payments", label: "Payments" },
      { key: "shifts", label: "Shifts & Wages" },
      { key: "reports", label: "Reports" },
    ],
  },
  {
    label: "Arecanut",
    pages: [
      { key: "farmers", label: "Farmers" },
      { key: "dehusk", label: "Dehusk" },
    ],
  },
];

function AdminLogin({ onSuccess }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (pin.length < 4 || loading) return;
    setLoading(true);
    try {
      const { data } = await adminApi.post("/login", { pin });
      setAdminToken(data.token);
      onSuccess();
    } catch (e) {
      toast.error(e?.response
        ? (e.response.data?.detail || "Incorrect PIN")
        : "Can't reach the server -- check your connection");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-full flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-xs" data-testid="admin-login">
        <div className="flex flex-col items-center mb-8">
          <div className="h-14 w-14 rounded-xl bg-amber-400 flex items-center justify-center mb-3">
            <ShieldCheck size={28} weight="bold" className="text-slate-900" />
          </div>
          <h1 className="text-white text-xl font-semibold tracking-tight">AgriLedger Admin</h1>
          <p className="text-slate-400 text-sm mt-1">Platform operator access only</p>
        </div>
        <Input
          data-testid="admin-pin-input"
          type="password"
          inputMode="numeric"
          maxLength={6}
          autoFocus
          className="font-data text-center text-lg h-12 bg-slate-800 border-slate-700 text-white"
          placeholder="PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        />
        <Button data-testid="admin-pin-submit" disabled={pin.length < 4 || loading} onClick={submit}
          className="w-full mt-4 h-12 bg-amber-400 hover:bg-amber-500 text-slate-900 font-semibold disabled:opacity-40">
          {loading ? "Checking…" : "Unlock"}
        </Button>
      </div>
    </div>
  );
}

// Shared by Create + Edit -- pages / pricing / deduction pickers.
function TenantPagesAndModes({ form, setForm }) {
  const togglePage = (key) => {
    setForm((f) => ({
      ...f,
      enabled_pages: f.enabled_pages.includes(key)
        ? f.enabled_pages.filter((p) => p !== key)
        : [...f.enabled_pages, key],
    }));
  };

  return (
    <>
      <div className="space-y-2">
        <Label>Enabled Pages</Label>
        {PAGE_GROUPS.map((g) => (
          <div key={g.label} className="border border-slate-200 rounded-md p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{g.label}</div>
            <div className="grid grid-cols-2 gap-2">
              {g.pages.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    data-testid={`page-${p.key}`}
                    checked={form.enabled_pages.includes(p.key)}
                    onCheckedChange={() => togglePage(p.key)}
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {form.enabled_pages.includes("farmers") && (
        <div className="space-y-1.5">
          <Label>Arecanut Pricing Mode</Label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "quintal", label: "Quintal", desc: "Price directly per raw quintal" },
              { value: "dried", label: "Dried Conversion", desc: "Price per kg of dried/processed yield" },
            ].map((opt) => (
              <label key={opt.value} data-testid={`pricing-mode-${opt.value}`}
                className={`border rounded-md p-3 cursor-pointer text-sm transition-colors ${
                  form.arecanut_pricing_mode === opt.value
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}>
                <input type="radio" name="pricing_mode" className="sr-only"
                  checked={form.arecanut_pricing_mode === opt.value}
                  onChange={() => setForm({ ...form, arecanut_pricing_mode: opt.value })} />
                <div className="font-medium text-slate-800">{opt.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
              </label>
            ))}
          </div>
        </div>
      )}

      {form.enabled_pages.includes("farmers") && (
        <div className="space-y-1.5">
          <Label>Bag Tare / Sodi Deduction</Label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "separate", label: "Separate", desc: "Bag tare (kg/bag) and sodi (kg/quintal) deducted independently" },
              { value: "combined", label: "Combined", desc: "One deduction rate (kg/quintal) covers both bag weight and wastage" },
            ].map((opt) => (
              <label key={opt.value} data-testid={`deduction-mode-${opt.value}`}
                className={`border rounded-md p-3 cursor-pointer text-sm transition-colors ${
                  form.arecanut_deduction_mode === opt.value
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}>
                <input type="radio" name="deduction_mode" className="sr-only"
                  checked={form.arecanut_deduction_mode === opt.value}
                  onChange={() => setForm({ ...form, arecanut_deduction_mode: opt.value })} />
                <div className="font-medium text-slate-800">{opt.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{opt.desc}</div>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function CreateTenantDialog({ open, onOpenChange, onCreated }) {
  const empty = { slug: "", name: "", pin: "", enabled_pages: [], arecanut_pricing_mode: "quintal", arecanut_deduction_mode: "separate" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.slug.trim() || !form.name.trim() || !form.pin.trim() || form.enabled_pages.length === 0) {
      toast.error("Fill in Business ID, Name, PIN and pick at least one page");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/tenants", form);
      toast.success(`Business "${data.name}" created — give them ID "${data.slug}" and the PIN to log in`);
      setForm(empty);
      onOpenChange(false);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create business");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setForm(empty); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader><DialogTitle>Add Business</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Business ID (login slug)</Label>
              <Input data-testid="tenant-slug" value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                placeholder="ravi-farms" />
            </div>
            <div className="space-y-1.5">
              <Label>PIN (4–6 digits)</Label>
              <Input data-testid="tenant-pin" inputMode="numeric" value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                placeholder="1234" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Business Name</Label>
            <Input data-testid="tenant-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ravi Farms" />
          </div>

          <TenantPagesAndModes form={form} setForm={setForm} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="btn-create-tenant" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={submit}>
            {saving ? "Creating…" : "Create Business"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditTenantDialog({ tenant, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tenant) {
      setForm({
        name: tenant.name,
        enabled_pages: tenant.enabled_pages,
        arecanut_pricing_mode: tenant.arecanut_pricing_mode,
        arecanut_deduction_mode: tenant.arecanut_deduction_mode,
      });
    } else {
      setForm(null);
    }
  }, [tenant]);

  const submit = async () => {
    if (!form.name.trim() || form.enabled_pages.length === 0) {
      toast.error("Business name and at least one page are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.put(`/tenants/${tenant.id}`, form);
      toast.success(`"${data.name}" updated`);
      onOpenChange(false);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update business");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Edit Business{tenant ? ` — ${tenant.slug}` : ""}</DialogTitle>
        </DialogHeader>
        {form && (
          <>
            <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label>Business Name</Label>
                <Input data-testid="edit-tenant-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <TenantPagesAndModes form={form} setForm={setForm} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="btn-save-tenant" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={submit}>
                {saving ? "Saving…" : "Save Changes"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResetPinDialog({ tenant, onOpenChange, onDone }) {
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPin(""); }, [tenant]);

  const submit = async () => {
    if (!(pin.length >= 4 && pin.length <= 6)) return;
    setSaving(true);
    try {
      await adminApi.post(`/tenants/${tenant.id}/reset-pin`, { pin });
      toast.success(`PIN reset for "${tenant.name}" — they'll be asked to change it on next login`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset PIN");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle>Reset PIN{tenant ? ` — ${tenant.slug}` : ""}</DialogTitle>
          <DialogDescription>
            Sets a new temporary PIN. They'll be required to change it on their next login.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-1.5">
          <Label>New PIN (4–6 digits)</Label>
          <Input data-testid="reset-pin-input" inputMode="numeric" autoFocus value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="1234" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="btn-confirm-reset-pin" disabled={saving || pin.length < 4} onClick={submit}
            className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? "Saving…" : "Reset PIN"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleActiveDialog({ tenant, onOpenChange, onDone }) {
  const [saving, setSaving] = useState(false);
  if (!tenant) return null;
  const willActivate = !tenant.active;

  const confirm = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/tenants/${tenant.id}/active`, { active: willActivate });
      toast.success(willActivate ? `"${tenant.name}" reactivated — they can log in again` : `"${tenant.name}" deactivated — login is now blocked`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update business");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={!!tenant} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{willActivate ? "Reactivate" : "Deactivate"} "{tenant.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            {willActivate
              ? "They'll be able to log in again immediately. All their data is untouched."
              : "Login is blocked immediately. All their data is kept and this is fully reversible."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
          <Button data-testid="btn-confirm-toggle-active" disabled={saving} onClick={confirm}
            className={willActivate ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 hover:bg-slate-800"}>
            {saving ? "Working…" : willActivate ? "Reactivate" : "Deactivate"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EraseTenantDialog({ tenant, onOpenChange, onDone }) {
  const [confirmSlug, setConfirmSlug] = useState("");
  const [erasing, setErasing] = useState(false);

  useEffect(() => { setConfirmSlug(""); }, [tenant]);

  const submit = async () => {
    if (confirmSlug !== tenant.slug) return;
    setErasing(true);
    try {
      await adminApi.delete(`/tenants/${tenant.id}`);
      toast.success(`"${tenant.name}" and all its records were permanently erased`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not erase business");
    } finally {
      setErasing(false);
    }
  };

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-rose-700">Erase "{tenant?.name}" Permanently</DialogTitle>
          <DialogDescription>
            This cannot be undone. Every record for this business — employees, attendance,
            payments, farmers, harvests, advances, dehusk data and more — will be wiped.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-1.5">
          <Label>Type <span className="font-data font-semibold">{tenant?.slug}</span> to confirm</Label>
          <Input data-testid="erase-confirm-input" autoFocus value={confirmSlug}
            onChange={(e) => setConfirmSlug(e.target.value)}
            placeholder={tenant?.slug} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="btn-confirm-erase" variant="destructive"
            disabled={erasing || confirmSlug !== tenant?.slug} onClick={submit}>
            {erasing ? "Erasing…" : "Erase Permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const AUDIT_LABELS = {
  employees: "Labourers", attendance: "Attendance days", payments: "Payments", lends: "Lends",
  farmers: "Farmers", harvests: "Harvest lots", settlements: "Settlements",
  workers: "Dehusk workers", entries: "Dehusk entries", advances: "Advances",
};

function AuditDialog({ tenant, onOpenChange }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenant) { setAudit(null); return; }
    setLoading(true);
    adminApi.get(`/tenants/${tenant.id}/audit`)
      .then((r) => setAudit(r.data))
      .catch(() => toast.error("Could not load audit"))
      .finally(() => setLoading(false));
  }, [tenant]);

  const moneyRows = audit ? [
    ["Labour payments", audit.money.labour_payments],
    ["Labour lends", audit.money.labour_lends],
    ["Labour advances", audit.money.labour_advances],
    ["Farmer advances", audit.money.farmer_advances],
    ["Farmer harvest value", audit.money.farmer_harvest_value],
    ["Dehusk paid", audit.money.dehusk_paid],
    ["Dehusk advances", audit.money.dehusk_advances],
  ] : [];

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Audit{tenant ? ` — ${tenant.name}` : ""}</DialogTitle>
          <DialogDescription>Activity snapshot for statements and records. Seeded defaults are excluded.</DialogDescription>
        </DialogHeader>
        {loading && <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>}
        {!loading && audit && !audit.has_activity && (
          <div data-testid="audit-empty" className="py-10 text-center text-slate-400 text-sm">No records yet.</div>
        )}
        {!loading && audit && audit.has_activity && (
          <div className="space-y-5 py-2 max-h-[65vh] overflow-y-auto pr-1" data-testid="audit-content">
            <div className="text-xs text-slate-500">
              Activity from <span className="font-data font-medium text-slate-700">{audit.activity_from}</span> to{" "}
              <span className="font-data font-medium text-slate-700">{audit.activity_to}</span>
            </div>

            {Object.entries(audit.counts).map(([group, rows]) => (
              <div key={group}>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{group}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {Object.entries(rows).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-1">
                      <span className="text-slate-600">{AUDIT_LABELS[k] || k}</span>
                      <span className="font-data font-medium">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Money Moved</div>
              <div className="space-y-1 text-sm">
                {moneyRows.map(([label, val]) => (
                  <div key={label} className="flex justify-between border-b border-slate-100 py-1">
                    <span className="text-slate-600">{label}</span>
                    <span className="font-data font-medium">{fmtMoney(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminApp() {
  const [authed, setAuthed] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [pinTarget, setPinTarget] = useState(null);
  const [activeTarget, setActiveTarget] = useState(null);
  const [eraseTarget, setEraseTarget] = useState(null);
  const [auditTarget, setAuditTarget] = useState(null);

  const load = () => adminApi.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});

  useEffect(() => {
    const onUnauth = () => setAuthed(false);
    window.addEventListener("agri-admin-unauth", onUnauth);
    if (!getAdminToken()) {
      setAuthed(false);
    } else {
      adminApi.get("/tenants")
        .then((r) => { setTenants(r.data); setAuthed(true); })
        .catch(() => setAuthed(false));
    }
    return () => window.removeEventListener("agri-admin-unauth", onUnauth);
  }, []);

  const logout = () => { clearAdminToken(); setAuthed(false); };

  if (authed === null) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-900 text-slate-400 font-data text-sm">
        Loading…
      </div>
    );
  }

  if (!authed) {
    return (
      <>
        <AdminLogin onSuccess={() => { setAuthed(true); load(); }} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white h-16 flex items-center justify-between px-6">
        <div className="flex items-center gap-2 font-semibold">
          <ShieldCheck size={20} weight="bold" className="text-amber-400" /> AgriLedger — Platform Admin
        </div>
        <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
          <SignOut size={16} /> Log out
        </button>
      </header>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Businesses</h1>
            <p className="text-sm text-slate-500">{tenants.length} business{tenants.length === 1 ? "" : "es"} provisioned</p>
          </div>
          <Button data-testid="btn-add-business" className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => setCreateOpen(true)}>
            <Plus size={16} weight="bold" /> Add Business
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Business ID</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Enabled Pages</th>
                <th className="text-left px-4 py-3">Pricing Mode</th>
                <th className="text-left px-4 py-3">Deduction Mode</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.slug}`} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-data">{t.slug}</td>
                  <td className="px-4 py-3">{t.name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{t.enabled_pages.join(", ")}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{t.enabled_pages.includes("farmers") ? t.arecanut_pricing_mode : "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{t.enabled_pages.includes("farmers") ? t.arecanut_deduction_mode : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${t.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {t.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`btn-edit-${t.slug}`} title="Edit" onClick={() => setEditTarget(t)}
                        className="h-7 w-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <PencilSimple size={15} />
                      </button>
                      <button data-testid={`btn-reset-pin-${t.slug}`} title="Reset PIN" onClick={() => setPinTarget(t)}
                        className="h-7 w-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <LockKey size={15} />
                      </button>
                      <button data-testid={`btn-audit-${t.slug}`} title="Audit" onClick={() => setAuditTarget(t)}
                        className="h-7 w-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                        <ChartBar size={15} />
                      </button>
                      <button data-testid={`btn-toggle-active-${t.slug}`} title={t.active ? "Deactivate" : "Reactivate"} onClick={() => setActiveTarget(t)}
                        className={`h-7 w-7 flex items-center justify-center rounded-md hover:bg-slate-100 ${t.active ? "text-slate-500 hover:text-slate-700" : "text-emerald-600 hover:text-emerald-700"}`}>
                        <Power size={15} />
                      </button>
                      <button data-testid={`btn-erase-${t.slug}`} title={t.active ? "Deactivate first to erase" : "Erase permanently"}
                        disabled={t.active} onClick={() => setEraseTarget(t)}
                        className="h-7 w-7 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent">
                        <Trash size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No businesses yet. Click "Add Business" to create the first one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      <EditTenantDialog tenant={editTarget} onOpenChange={(o) => !o && setEditTarget(null)} onSaved={load} />
      <ResetPinDialog tenant={pinTarget} onOpenChange={(o) => !o && setPinTarget(null)} onDone={load} />
      <ToggleActiveDialog tenant={activeTarget} onOpenChange={(o) => !o && setActiveTarget(null)} onDone={load} />
      <EraseTenantDialog tenant={eraseTarget} onOpenChange={(o) => !o && setEraseTarget(null)} onDone={load} />
      <AuditDialog tenant={auditTarget} onOpenChange={(o) => !o && setAuditTarget(null)} />
      <Toaster position="top-right" richColors />
    </div>
  );
}
